import { Amount } from 'src_common/common/amount/Amount';

function getPastDateByDays(days: number): Date {
    return new Date(Date.now() - days * 24 * 60 * 60 * 1000);
}

interface QueryType {
    searchBy?: string;
    searchByIdValue?: string;
    'betType[EW]'?: boolean;
    page?: number;
    sort?: string[];
    perPage?: number;
    summary?: boolean;
    range?: {
        startDate: string;
        endDate: string;
    };
    type?: string;
    selection?: string;
    event?: string;
    accountId?: string;
    freebet?: boolean;
    cashedOut?: string;
    settleType?: string;
    inPlay?: string;
    totalStake?: {
        start?: string;
        end?: string;
    };
    payoutAmount?: {
        start?: string;
        end?: string;
    };
    stakeFactor?: {
        start?: string;
        end?: string;
    };
    sport?: Array<{
        id: string;
        competitions: boolean | Array<string>;
    }>;
    history?: boolean;
    // Leftover for any other query param uknown atm.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

interface BetsRequestQueryType {
    perPage: number;
    // Leftover for any other query param uknown atm.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

export function prepareBetsRequestQuery(query: QueryType): BetsRequestQueryType {
    const q: BetsRequestQueryType = {
        perPage: 10,
    };

    if (query.event !== undefined) {
        q['leg.event'] = query.event;
    }

    if (query.searchBy === 'eventId' && query.searchByIdValue !== undefined) {
        q['leg.event'] = {
            in: [query.searchByIdValue],
        };
    }

    if (query.searchBy === 'betId' && query.searchByIdValue !== undefined) {
        q['betId'] = {
            in: [query.searchByIdValue],
        };
    }

    if (query['betType[EW]'] !== undefined && query['betType[EW]'] === true) {
        q['eachWay'] = {
            eq: true,
        };
        query['betType[EW]'] = false;
    }

    if (query.page !== undefined) {
        q.page = query.page;
    }

    if (query.sort !== undefined) {
        q.sort = query.sort;
    }

    if (query.perPage !== undefined) {
        q.perPage = query.perPage;
    }

    if (query.summary !== undefined && query.summary === true) {
        q.aggregations = {
            total: {
                type: 'count',
            },
            totalProfitLoss: {
                type: 'sum',
                field: 'operatorProfit',
            },
            totalStake: {
                type: 'sum',
                field: 'operatorTotalStake',
            },
            averageStake: {
                type: 'avg',
                field: 'operatorTotalStake',
            },
        };
    } else {
        q.aggregations = {
            total: {
                type: 'count',
            },
        };
    }

    // Date (placement / settlement)
    if (query.range !== undefined) {
        const date = {
            gte: query.range.startDate,
            lte: query.range.endDate,
        };

        if (query.type === 'placement') {
            q.placedAt = date;
        } else {
            q.settledAt = date;
        }
    }

    // Selection ID
    if (query.selection !== undefined) {
        q.selection = parseInt(query.selection, 10);
    }

    // Account ID
    if (query.accountId !== undefined) {
        q['account.id'] = {
            eq: parseFloat(query.accountId),
        };
    }

    if (query.freebet !== undefined) {
        q['freebet'] = {
            eq: query.freebet,
        };
    }

    // Cashed out
    if (query.cashedOut !== undefined && query.cashedOut !== 'both') {
        q['cashOut'] = {
            eq: query.cashedOut === 'yes',
        };
    }

    // Settle type
    if (query.settleType !== undefined && query.settleType.trim() !== '') {
        q['settleType'] = {
            eq: query.settleType,
        };
    }

    // In play
    if (query.inPlay !== undefined && query.inPlay !== 'both') {
        q['leg.inPlay'] = {
            eq: query.inPlay === 'yes',
        };
    }

    // Status
    const statuses = Object.keys(query)
        .filter((x) => x.startsWith('status[') && query[x])
        .map((x) => x.substring(7).split(']')[0]);

    if (statuses.includes('open')) {
        statuses.push('unsettled');
    }

    if (statuses.includes('cancelled')) {
        statuses.push('rejected');
    }

    if (statuses.length > 0) {
        q['status'] = {
            in: statuses,
        };
    }

    // Total stake
    if (query.totalStake !== undefined) {
        q['operatorTotalStake'] = {};
        if (query.totalStake.start !== undefined && query.totalStake.start !== '') {
            q['operatorTotalStake'].gte = $appState.configState.precision.valueOldFormat(
                new Amount(query.totalStake.start)
            );
        }

        if (query.totalStake.end !== undefined && query.totalStake.end !== '') {
            q['operatorTotalStake'].lte = $appState.configState.precision.valueOldFormat(
                new Amount(query.totalStake.end)
            );
        }
    }

    // Payout
    if (query.payoutAmount !== undefined) {
        q['operatorPayout'] = {};

        if (query.payoutAmount.start !== undefined && query.payoutAmount.start !== '') {
            q['operatorPayout'].gte = $appState.configState.precision.valueOldFormat(
                new Amount(query.payoutAmount.start)
            );
        }

        if (query.payoutAmount.end !== undefined && query.payoutAmount.end !== '') {
            q['operatorPayout'].lte = $appState.configState.precision.valueOldFormat(
                new Amount(query.payoutAmount.end)
            );
        }
    }

    // Channel
    const channels: string[] = [];
    Object.keys(query)
        .filter((x) => x.includes('channel') && query[x])
        .map((x) =>
            x
                .slice(x.indexOf('[') + 1, x.indexOf(']'))
                .split(',')
                .forEach((x) => channels.push(x))
        );

    if (channels.length > 0) {
        q['channel'] = {
            in: channels,
        };
    }

    // Bet type
    const betTypes: (string | undefined)[] = Object.keys(query)
        .filter((x) => x.startsWith('betType[') && query[x])
        .map((x) => x.substring(8).split(']')[0]);

    if (betTypes.length > 0) {
        q['type'] = {
            in: betTypes,
        };
    }

    // Stake factor
    if (query.stakeFactor !== undefined) {
        q['stakeFactor'] = {};

        if (query.stakeFactor.start !== undefined) {
            q['stakeFactor'].gte = parseFloat(query.stakeFactor.start);
        }

        if (query.stakeFactor.end !== undefined) {
            q['stakeFactor'].lte = parseFloat(query.stakeFactor.end);
        }
    }

    // Sports & competitions
    if (query.sport !== undefined && query.sport.length > 0) {
        let competitions: number[] = [];
        const sports = [];

        const toInt = (x: string): number => parseInt(x, 10);

        for (const sport of query.sport) {
            if (sport.competitions === true) {
                sports.push(sport.id);
            } else if (sport.competitions !== false && sport.competitions.length > 0) {
                sports.push(sport.id);
                competitions = competitions.concat(sport.competitions.map(toInt));
            }
        }

        if (sports.length > 0) {
            q['leg.sport'] = {
                in: sports,
            };
        }

        if (competitions.length > 0) {
            q['leg.competition'] = {
                in: competitions,
            };
        }
    }

    if (query.history !== undefined && query.history === true) {
        const today = new Date();
        q['placedAt'] = {
            gte: getPastDateByDays(7),
            lte: today,
        };
        q['status'] = {
            in: ['open', 'parked', 'settled', 'cancelled', 'unsettled', 'rejected', 'resulted'],
        };
    }

    return q;
}
